import { sedrApi, type SedrPhase } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';
import { phaseOptionHtml } from './sedrPhases';
import { loadSedrCategories, renderCategorySelectOptions } from './sedrUi';
import {
  approvalValidityFieldHtml,
  bindApprovalGraceVisibility,
  parseApprovalValidityFromForm,
} from './sedrApprovalValidity';

export function showSedrCreateRequirementDialog(options: {
  roomId: string;
  phases: SedrPhase[];
  onCreated: () => void | Promise<void>;
}): void {
  const shell = createModalShell({
    titleKey: 'sedr.requirement.createTitle',
    titleId: 'sedr-create-req-title',
    helpTopic: dialogHelpTopic('sedrCreateRequirement'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-create-req-form">
      <label>
        <span data-i18n="sedr.table.reqId"></span>
        <input name="requirementCode" required maxlength="64" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.table.title"></span>
        <input name="title" required maxlength="500" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.table.description"></span>
        <textarea name="description" maxlength="5000" rows="3"></textarea>
      </label>
      <label>
        <span data-i18n="sedr.requirement.category"></span>
        <select name="category"></select>
      </label>
      <label>
        <span data-i18n="sedr.table.owner"></span>
        <input name="ownerName" maxlength="200" autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.phases.field"></span>
        <select name="phaseId">
          ${phaseOptionHtml(options.phases, null)}
        </select>
      </label>
      ${approvalValidityFieldHtml()}
      <p class="error-text" id="sedr-create-req-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'sedr-create-req-cancel';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.id = 'sedr-create-req-submit';
  submitButton.setAttribute('form', 'sedr-create-req-form');
  submitButton.setAttribute('data-i18n', 'sedr.requirement.create');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);
  bindApprovalGraceVisibility(shell.body);

  const form = shell.body.querySelector('#sedr-create-req-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-create-req-error') as HTMLParagraphElement;
  const codeInput = form.querySelector('input[name="requirementCode"]') as HTMLInputElement;
  const categorySelect = form.querySelector('select[name="category"]') as HTMLSelectElement;
  let submitting = false;
  let lastErrorKey = 'status.error';

  void loadSedrCategories(options.roomId).then((categories) => {
    renderCategorySelectOptions(categorySelect, categories, categories[0]?.id);
  });

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  codeInput.focus();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      const data = new FormData(form);
      try {
        const phaseId = String(data.get('phaseId') ?? '').trim() || null;
        const validity = parseApprovalValidityFromForm(data);
        await sedrApi.createRequirement(options.roomId, {
          requirementCode: String(data.get('requirementCode') ?? '').trim(),
          title: String(data.get('title') ?? '').trim(),
          description: String(data.get('description') ?? '').trim() || null,
          category: String(data.get('category') ?? 'StaticGovernance'),
          ownerName: String(data.get('ownerName') ?? '').trim() || null,
          status: 'PendingUpload',
          phaseId,
          approvalValidityAmount: validity.approvalValidityAmount,
          approvalValidityUnit: validity.approvalValidityUnit,
          approvalGraceDays: validity.approvalGraceDays,
        });
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onCreated();
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
